import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../ui/select';
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from '../ui/dialog';
import { Label } from '../ui/label';
import { Plus, Loader2, Search, Building2, MoreVertical, KeyRound, RefreshCcw, PowerOff, Power } from 'lucide-react';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import {
  useAdminClient, useAdminClients,
  useActivateClient, useRenewClient, useResetAdminPassword, useSuspendClient,
} from '../../hooks/api/admin';
import { ClientOnboarding } from './ClientOnboarding';
import { ProjectCatalogManager } from './ProjectCatalogManager';

function fmtBytes(n) {
  if (!n && n !== 0) return '—';
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  if (n < 1024 * 1024 * 1024) return `${(n / 1024 / 1024).toFixed(1)} MB`;
  return `${(n / 1024 / 1024 / 1024).toFixed(2)} GB`;
}

function StatusPill({ status }) {
  const map = {
    active: 'bg-emerald-500/15 text-emerald-300 border-emerald-500/30',
    trial: 'bg-blue-500/15 text-blue-300 border-blue-500/30',
    inactive: 'bg-slate-500/15 text-slate-300 border-slate-500/30',
    suspended: 'bg-red-500/15 text-red-300 border-red-500/30',
  };
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs border ${map[status] || ''}`}>
      {status}
    </span>
  );
}

// Decide whether the user landed here intending to onboard a new client
// (e.g. they hit /register, /admin/clients/new, /onboarding) or to manage
// the existing list (/admin/clients).
function initialModeFromUrl() {
  if (typeof window === 'undefined') return 'list';
  const p = window.location.pathname.toLowerCase();
  if (
    /\/new\/?$/.test(p) ||
    /^\/register(ation)?\/?$/.test(p) ||
    /^\/onboard(ing)?\/?$/.test(p)
  ) {
    return 'onboard';
  }
  return 'list';
}

function pushAdminUrl(mode) {
  if (typeof window === 'undefined') return;
  const target = mode === 'onboard' ? '/admin/clients/new' : '/admin/clients';
  if (window.location.pathname !== target) {
    window.history.pushState({ mode }, '', target);
  }
}

export function ClientManagement() {
  const [mode, setModeRaw] = useState(initialModeFromUrl);  // 'list' | 'onboard' | 'detail'
  const [activeClientId, setActiveClientId] = useState(null);
  const [q, setQ] = useState('');
  const [statusFilter, setStatusFilter] = useState('');

  // Wrap setMode so URL stays in sync as the user toggles list <-> onboard
  const setMode = React.useCallback((next) => {
    setModeRaw(next);
    if (next === 'onboard' || next === 'list') pushAdminUrl(next);
  }, []);

  const { data, isLoading, isError, error } = useAdminClients({
    q: q || undefined,
    status: statusFilter || undefined,
    page: 1,
    page_size: 100,
  });

  if (mode === 'onboard') {
    return (
      <div className="space-y-4">
        <Button variant="outline" onClick={() => setMode('list')}>← Back to clients</Button>
        <ClientOnboarding
          existingClientCount={data?.total || 0}
          onSuccess={() => setMode('list')}
        />
      </div>
    );
  }

  if (mode === 'detail' && activeClientId) {
    return (
      <ClientDetail
        clientId={activeClientId}
        onBack={() => { setMode('list'); setActiveClientId(null); }}
      />
    );
  }

  return (
    <div className="space-y-4">
      <header className="flex items-center justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-semibold flex items-center gap-2">
            <Building2 className="w-6 h-6" /> Clients
          </h1>
          <p className="text-sm text-muted-foreground">
            Manage tenants, contracts, and module access.
          </p>
        </div>
        <Button onClick={() => setMode('onboard')}>
          <Plus className="w-4 h-4 mr-2" /> Onboard Client
        </Button>
      </header>

      <Card>
        <CardContent className="pt-4">
          <div className="flex items-center gap-2 mb-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Search by code, name, or company"
                className="pl-9"
              />
            </div>
            <Select value={statusFilter || 'all'} onValueChange={(v) => setStatusFilter(v === 'all' ? '' : v)}>
              <SelectTrigger className="w-40"><SelectValue placeholder="All statuses" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All statuses</SelectItem>
                <SelectItem value="active">Active</SelectItem>
                <SelectItem value="trial">Trial</SelectItem>
                <SelectItem value="inactive">Inactive</SelectItem>
                <SelectItem value="suspended">Suspended</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {isLoading && (
            <div className="flex items-center gap-2 py-8 justify-center text-sm text-muted-foreground">
              <Loader2 className="w-4 h-4 animate-spin" /> Loading clients…
            </div>
          )}
          {isError && (
            <div className="py-6 text-sm text-destructive">
              Failed to load clients: {error?.message}
            </div>
          )}
          {!isLoading && !isError && (data?.data?.length ?? 0) === 0 && (
            <div className="py-10 text-center text-sm text-muted-foreground">
              No clients yet. Click <strong>Onboard Client</strong> to provision your first tenant.
            </div>
          )}

          {!isLoading && (data?.data?.length ?? 0) > 0 && (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="text-muted-foreground">
                  <tr className="border-b border-border">
                    <th className="text-left py-2 pr-4">Code</th>
                    <th className="text-left py-2 pr-4">Name</th>
                    <th className="text-left py-2 pr-4">Status</th>
                    <th className="text-left py-2 pr-4">Tier</th>
                    <th className="text-left py-2 pr-4">Contract</th>
                    <th className="text-right py-2 pr-2"></th>
                  </tr>
                </thead>
                <tbody>
                  {data.data.map((c) => (
                    <tr key={c.id} className="border-b border-border/40 hover:bg-accent/40">
                      <td className="py-2 pr-4 font-mono">{c.code}</td>
                      <td className="py-2 pr-4">
                        <div className="font-medium">{c.name}</div>
                        {c.company_name && (
                          <div className="text-xs text-muted-foreground">{c.company_name}</div>
                        )}
                      </td>
                      <td className="py-2 pr-4"><StatusPill status={c.status} /></td>
                      <td className="py-2 pr-4">{c.contract_tier || '—'}</td>
                      <td className="py-2 pr-4 text-xs">
                        {c.contract_start || '—'} → {c.contract_end || '—'}
                      </td>
                      <td className="py-2 pr-2 text-right">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => { setActiveClientId(c.id); setMode('detail'); }}
                        >
                          Manage
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function ClientDetail({ clientId, onBack }) {
  const { data, isLoading, isError, error } = useAdminClient(clientId);
  const suspend = useSuspendClient();
  const activate = useActivateClient();
  const renew = useRenewClient();
  const resetPwd = useResetAdminPassword();

  const [renewOpen, setRenewOpen] = useState(false);
  const [newEnd, setNewEnd] = useState('');
  const [pwdDialog, setPwdDialog] = useState(null); // userId
  const [newPwd, setNewPwd] = useState('');

  if (isLoading) return <div className="p-6 text-sm text-muted-foreground"><Loader2 className="w-4 h-4 animate-spin inline mr-2" />Loading…</div>;
  if (isError) return <div className="p-6 text-sm text-destructive">Failed: {error?.message}</div>;
  if (!data) return null;

  const c = data;
  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <Button variant="outline" onClick={onBack}>← Back</Button>
        <div className="flex gap-2">
          {c.status === 'suspended' || c.status === 'inactive' ? (
            <Button variant="outline" onClick={() => activate.mutate(clientId)}>
              <Power className="w-4 h-4 mr-2" /> Activate
            </Button>
          ) : (
            <Button variant="outline" onClick={() => suspend.mutate(clientId)}>
              <PowerOff className="w-4 h-4 mr-2" /> Suspend
            </Button>
          )}
          <Button variant="outline" onClick={() => setRenewOpen(true)}>
            <RefreshCcw className="w-4 h-4 mr-2" /> Renew Contract
          </Button>
        </div>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-3">
            <span className="font-mono">{c.code}</span> {c.name}
            <StatusPill status={c.status} />
          </CardTitle>
        </CardHeader>
        <CardContent className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
          <KV k="Company" v={c.company_name} />
          <KV k="Contact" v={c.contact_name} />
          <KV k="Email" v={c.contact_email} />
          <KV k="Phone" v={c.contact_phone} />
          <KV k="Country" v={c.country} />
          <KV k="Tier" v={c.contract_tier} />
          <KV k="Contract Start" v={c.contract_start} />
          <KV k="Contract End" v={c.contract_end} />
          <KV k="S3 Prefix" v={c.s3_prefix} mono />
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle>Usage</CardTitle></CardHeader>
        <CardContent className="grid grid-cols-2 md:grid-cols-5 gap-4 text-sm">
          <Stat k="Inspections" v={c.usage?.inspections_total} />
          <Stat k="Active" v={c.usage?.inspections_active} />
          <Stat k="Files" v={c.usage?.files_total} />
          <Stat k="Reports" v={c.usage?.reports_total} />
          <Stat k="Storage" v={fmtBytes(c.usage?.storage_bytes_estimate)} />
        </CardContent>
      </Card>

      {/* Project catalog — pick from existing or create new with auto-coded IDs */}
      <Card>
        <CardHeader>
          <CardTitle>Project Catalog</CardTitle>
        </CardHeader>
        <CardContent>
          <ProjectCatalogManager
            mode="manage"
            clientId={c.id}
            clientCode={c.code}
            enabledProjectIds={c.project_ids || []}
          />
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle>Members ({c.members?.length ?? 0})</CardTitle></CardHeader>
        <CardContent>
          {(c.members?.length ?? 0) === 0 ? (
            <p className="text-sm text-muted-foreground">No members yet.</p>
          ) : (
            <table className="w-full text-sm">
              <thead className="text-muted-foreground">
                <tr className="border-b border-border">
                  <th className="text-left py-2 pr-4">Name</th>
                  <th className="text-left py-2 pr-4">Email</th>
                  <th className="text-left py-2 pr-4">Role</th>
                  <th className="text-left py-2 pr-4">Status</th>
                  <th className="text-right py-2 pr-2"></th>
                </tr>
              </thead>
              <tbody>
                {c.members.map((m) => (
                  <tr key={m.user_id} className="border-b border-border/40">
                    <td className="py-2 pr-4">{m.full_name}</td>
                    <td className="py-2 pr-4 font-mono text-xs">{m.email}</td>
                    <td className="py-2 pr-4">
                      {m.is_client_admin ? <Badge>Client Admin</Badge> : <Badge variant="secondary">User</Badge>}
                    </td>
                    <td className="py-2 pr-4">
                      <StatusPill status={m.is_active ? 'active' : 'inactive'} />
                    </td>
                    <td className="py-2 pr-2 text-right">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="sm"><MoreVertical className="w-4 h-4" /></Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => { setPwdDialog(m.user_id); setNewPwd(''); }}>
                            <KeyRound className="w-4 h-4 mr-2" />
                            Reset password
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </CardContent>
      </Card>

      {/* Renew dialog */}
      <Dialog open={renewOpen} onOpenChange={setRenewOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Renew contract</DialogTitle></DialogHeader>
          <div className="space-y-2">
            <Label>New end date</Label>
            <Input type="date" value={newEnd} onChange={(e) => setNewEnd(e.target.value)} />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRenewOpen(false)}>Cancel</Button>
            <Button
              disabled={!newEnd || renew.isPending}
              onClick={async () => {
                await renew.mutateAsync({ id: clientId, new_end_date: newEnd });
                setRenewOpen(false);
              }}
            >
              {renew.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Renew'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reset password dialog */}
      <Dialog open={Boolean(pwdDialog)} onOpenChange={() => setPwdDialog(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Reset member password</DialogTitle></DialogHeader>
          <div className="space-y-2">
            <Label>New temporary password</Label>
            <Input value={newPwd} onChange={(e) => setNewPwd(e.target.value)} placeholder="Min 8 chars" />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPwdDialog(null)}>Cancel</Button>
            <Button
              disabled={!newPwd || newPwd.length < 8 || resetPwd.isPending}
              onClick={async () => {
                await resetPwd.mutateAsync({ clientId, userId: pwdDialog, password: newPwd });
                setPwdDialog(null);
              }}
            >
              {resetPwd.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Reset'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function KV({ k, v, mono = false }) {
  return (
    <div>
      <div className="text-xs uppercase text-muted-foreground">{k}</div>
      <div className={mono ? 'font-mono text-xs break-all' : ''}>{v ?? '—'}</div>
    </div>
  );
}
function Stat({ k, v }) {
  return (
    <div>
      <div className="text-xs uppercase text-muted-foreground">{k}</div>
      <div className="text-xl font-semibold">{v ?? '—'}</div>
    </div>
  );
}
